import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { DayPicker, type ClassNames } from 'react-day-picker'
import { ru } from 'react-day-picker/locale'
import type { DatePickerProps } from './DatePicker.types'
import { applyDateMask, parseISO } from './DatePicker.utils'
import './DatePickerField.scss'

const DATE_FMT = 'DD.MM.YYYY'
const ISO_DATE = 'YYYY-MM-DD'

function parseDisplay(v: string, fmt = DATE_FMT): Date | null {
  const m = moment(v, fmt, true)
  return m.isValid() ? m.toDate() : null
}

function formatDisplay(d: Date, fmt = DATE_FMT): string {
  return moment(d).format(fmt)
}

function toISO(d: Date): string {
  return moment(d).format(ISO_DATE)
}

export const dpLocale = ru

export const LABELS = {
  dateMask: 'DD.MM.YYYY',
  timeMask: 'HH:MM:SS',
  placeholder: 'ДД.ММ.ГГГГ',
  timePlaceholder: 'ЧЧ:ММ:СС',
  clear: 'Очистить',
  dateLabel: 'Дата',
  timeLabel: 'Время',
  openCalendar: 'Открыть календарь',
}

/** DayPicker UI part → rd-calendar* class mapping. */
export const rdpClassNames: Partial<ClassNames> = {
  root: 'rd-calendar',
  months: 'rd-calendar__months',
  month: 'rd-calendar__month',
  month_caption: 'rd-calendar__caption',
  caption_label: 'rd-calendar__caption-label',
  nav: 'rd-calendar__nav',
  button_previous: 'rd-calendar__nav-btn rd-calendar__nav-btn--prev',
  button_next: 'rd-calendar__nav-btn rd-calendar__nav-btn--next',
  weekdays: 'rd-calendar__weekdays',
  weekday: 'rd-calendar__weekday',
  week: 'rd-calendar__week',
  day: 'rd-calendar__day',
  day_button: 'rd-calendar__day-btn',
  selected: 'rd-calendar__day--selected',
  today: 'rd-calendar__day--today',
  outside: 'rd-calendar__day--outside',
  disabled: 'rd-calendar__day--disabled',
  weeks_before_enter: 'rd-calendar__weeks--before-enter',
  weeks_before_exit: 'rd-calendar__weeks--before-exit',
  weeks_after_enter: 'rd-calendar__weeks--after-enter',
  weeks_after_exit: 'rd-calendar__weeks--after-exit',
  caption_before_enter: 'rd-calendar__caption--before-enter',
  caption_before_exit: 'rd-calendar__caption--before-exit',
  caption_after_enter: 'rd-calendar__caption--after-enter',
  caption_after_exit: 'rd-calendar__caption--after-exit',
}

export default function DatePickerField({
  name,
  label,
  placeholder,
  required,
  disabled,
  floating,
  size = 'medium',
  error,
  clearable,
  initialValue,
  onChange,
}: DatePickerProps) {
  const dateMask = LABELS.dateMask
  const [value, setValue] = useState<Date | null>(() => parseISO(initialValue ?? ''))
  const [inputText, setInputText] = useState(() => {
    const d = parseISO(initialValue ?? '')
    return d ? formatDisplay(d, dateMask) : ''
  })
  const [isOpen, setIsOpen] = useState(false)
  const [month, setMonth] = useState<Date>(() => value ?? new Date())
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const parsed = parseISO(initialValue ?? '')
    setValue(parsed)
    setInputText(parsed ? formatDisplay(parsed, dateMask) : '')
    setMonth(parsed ?? new Date())
  }, [initialValue, dateMask])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverPos(null)
      return
    }
    const updatePos = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      setPopoverPos({ top: rect.bottom + 4, left: rect.left })
    }
    updatePos()
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [isOpen])

  const commitDate = useCallback(
    (date: Date | null) => {
      setValue(date)
      setInputText(date ? formatDisplay(date, dateMask) : '')
      if (date) setMonth(date)
      onChange?.(date ? toISO(date) : '')
    },
    [onChange, dateMask],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isDelete = (e.nativeEvent as InputEvent).inputType?.startsWith('delete') ?? false
      const masked = applyDateMask(e.target.value, isDelete)
      setInputText(masked)
      if (!masked) {
        commitDate(null)
        return
      }
      const parsed = parseDisplay(masked, dateMask)
      if (parsed) commitDate(parsed)
    },
    [commitDate, dateMask],
  )

  const handleInputBlur = useCallback(
    (e: React.FocusEvent) => {
      if (containerRef.current?.contains(e.relatedTarget as Node)) return
      setInputText(value ? formatDisplay(value, dateMask) : '')
    },
    [value, dateMask],
  )

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        e.preventDefault()
      } else if (e.key === 'ArrowDown' && !isOpen) {
        setIsOpen(true)
        e.preventDefault()
      }
    },
    [isOpen],
  )

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      commitDate(date ?? null)
      setIsOpen(false)
      inputRef.current?.focus()
    },
    [commitDate],
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      commitDate(null)
      inputRef.current?.focus()
    },
    [commitDate],
  )

  const toggleCalendar = useCallback(() => {
    if (disabled) return
    setIsOpen(prev => !prev)
  }, [disabled])

  const hasValue = !!value || inputText.length > 0
  const showClear = clearable && hasValue && !disabled

  const wrapperCls = [
    'rd-datepicker',
    `rd-datepicker--${size}`,
    floating && 'rd-datepicker--floating',
    isOpen && 'rd-datepicker--opened',
    error && 'rd-datepicker--error',
    hasValue && 'has-value',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperCls} ref={containerRef}>
      {label && !floating && (
        <label className={`rd-datepicker__label${required ? ' rd-datepicker__label--required' : ''}`}>
          {label}
        </label>
      )}

      <div className="rd-datepicker__wrapper">
        <input
          ref={inputRef}
          type="text"
          className="rd-datepicker__field"
          value={inputText}
          placeholder={floating ? '' : (placeholder ?? LABELS.placeholder)}
          disabled={disabled}
          autoComplete="off"
          maxLength={dateMask.length}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
        />

        {name && <input type="hidden" name={name} value={value ? toISO(value) : ''} />}

        {floating && (
          <label className={`rd-datepicker__floating-label${required ? ' rd-datepicker__floating-label--required' : ''}`}>
            {label}
          </label>
        )}

        {showClear && (
          <button type="button" className="rd-datepicker__clear" onClick={handleClear} aria-label={LABELS.clear}>
            <span className="rd-icon-cross" />
          </button>
        )}

        <button
          type="button"
          className="rd-datepicker__calendar-btn"
          onClick={toggleCalendar}
          disabled={disabled}
          tabIndex={-1}
          aria-label={LABELS.openCalendar}
        >
          <span className="rd-icon-calendar" />
        </button>
      </div>

      {isOpen && popoverPos && createPortal(
        <div
          ref={popoverRef}
          className="rd-datepicker__popover rd-datepicker__popover--portal"
          role="dialog"
          aria-modal="false"
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <DayPicker
            mode="single"
            selected={value ?? undefined}
            onSelect={handleSelect}
            month={month}
            onMonthChange={setMonth}
            locale={dpLocale}
            weekStartsOn={1}
            showOutsideDays
            animate
            classNames={rdpClassNames}
          />
        </div>,
        document.body,
      )}

      {error && <span className="rd-datepicker__error-text">{error}</span>}
    </div>
  )
}
